import * as genericService from "./genericFormServices";
import type { LegalAdviceFormSixData } from "../types/LegalAdvices/LegalAdviceFormSix.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";

const ENDPOINT = "legal-advice-form-six";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormSixData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Six
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormSixData => {
  if (!item) return {} as LegalAdviceFormSixData;

  // Extract inner form values safely (added legalAdviceFormSix check)
  const form =
    item.legalAdviceFormSix ||
    item.legalAdviceForm ||
    item.formData ||
    {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_SIX',
    status: item.status || form.status || 'DRAFT',

    // Form Six Specific Text Fields
    office: form.office || "",
    letterNo: form.letterNo || "",
    year: form.year || "",
    month: form.month || "",
    day: form.day || "",
    refCourt: form.refCourt || "",
    refLetterDate: form.refLetterDate || "",
    refLetterNo: form.refLetterNo || "",
    caseTypeNo: form.caseTypeNo || "",
    complainant: form.complainant || "",
    plaintiff:form.plaintiff || "",
    accused: form.accused || "",
    law: form.law || "",
    lawSection: form.lawSection || "",
    advocateName: form.advocateName || "",
    serialNo: form.serialNo || "",
    officerSignName: form.officerSignName || "",
    ccRecipientName: form.ccRecipientName || "",
    ccRecipientSerial: form.ccRecipientSerial || "",
    ccTownship: form.ccTownship || "",
    ccJudge: form.ccJudge || "",
    ccJudgeCourt: form.ccJudgeCourt || "",
    ccTownshipJudge: form.ccTownshipJudge || "",
    signatureImage: formatSignatureUrl(form.signatureImage),
    signName: form.signName || item.signName || "",
    signerName: form.signerName || item.signerName || "",

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Six
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormSixData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;

  // Form Six Date Matching
  const formattedAppDate =
    app.refLetterDate ||
    (app.year && app.month && app.day ? `${app.year}-${app.month}-${app.day}` : '');

  const matchesDate = !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  // Search matching across Form Six fields
  const matchesGeneralSearch =
    !term ||
    Boolean(app.letterNo && app.letterNo.toLowerCase().includes(term)) ||
    Boolean(app.refLetterNo && app.refLetterNo.toLowerCase().includes(term)) ||
    Boolean(app.caseTypeNo && app.caseTypeNo.toLowerCase().includes(term)) ||
    Boolean(app.complainant && app.complainant.toLowerCase().includes(term)) ||
    Boolean(app.accused && app.accused.toLowerCase().includes(term)) ||
    Boolean(app.lawSection && app.lawSection.toLowerCase().includes(term)) ||
    Boolean(app.refCourt && app.refCourt.toLowerCase().includes(term)) ||
    Boolean(app.advocateName && app.advocateName.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormSixData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormSixData> =>
  genericService.fetchApplicationById<LegalAdviceFormSixData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormSixData>
) => genericService.saveDraft<LegalAdviceFormSixData>(ENDPOINT, id, data);

export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormSixData
) => genericService.submitForm<LegalAdviceFormSixData>(ENDPOINT, id, data);

export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);